import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { GitBranch, Users, ArrowRightLeft, TrendingUp, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { fmtUSD } from "@/lib/format";

/**
 * Market Dependency panel — replaces "Market Relationships" per v2.5A Epic 4.
 * Shows downstream/upstream markets: shared traders, same category,
 * inverse-direction markets, and price co-movement correlations.
 */
export default function MarketDependencies({ marketId, testid = "market-dependencies" }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["market-dependencies", marketId],
    queryFn: async () =>
      (await api.get(`/intel/markets/${marketId}/dependencies?limit=8`)).data,
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <section className="border border-zinc-800 rounded-md p-4 flex items-center gap-2 text-sm text-zinc-400"
               data-testid={testid}>
        <Loader2 size={14} className="animate-spin" />
        Mapping market dependencies…
      </section>
    );
  }
  if (isError || !data) return null;

  const anyData =
    (data.shared_trader_markets?.length || 0) +
    (data.inverse_markets?.length || 0) +
    (data.same_category_markets?.length || 0) +
    (data.price_correlated_markets?.length || 0);

  if (!anyData) {
    return (
      <section className="border border-zinc-800 rounded-md p-4" data-testid={testid}>
        <Header />
        <div className="text-xs text-zinc-500 mt-2 py-2">
          No dependency data yet. Cross-market activity will appear after more sync cycles.
        </div>
      </section>
    );
  }

  return (
    <section className="border border-zinc-800 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-800">
        <Header />
        <div className="text-xs text-zinc-500 mt-1">
          Downstream and correlated markets — where a thesis change here echoes elsewhere.
        </div>
      </div>

      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-800/70">
        <DepList
          testid={`${testid}-shared`}
          title="Shared traders"
          icon={Users}
          empty="No shared-trader markets."
          items={data.shared_trader_markets || []}
          render={(r) => (
            <RowInner
              title={r.market_title}
              subtitle={`${r.n_shared_traders} shared trader${r.n_shared_traders === 1 ? "" : "s"} · dominant: ${r.dominant_direction_here || "—"}${r.volume_24h ? ` · ${fmtUSD(r.volume_24h)} 24h` : ""}`}
              to={`/app/markets/${r.market_id}`}
            />
          )}
        />

        <DepList
          testid={`${testid}-inverse`}
          title="Inverse markets"
          icon={ArrowRightLeft}
          empty="No inverse-direction bets."
          items={data.inverse_markets || []}
          render={(r) => (
            <RowInner
              title={r.market_title}
              subtitle={`${r.n_shared_traders} shared, betting ${r.dominant_direction_here}`}
              to={`/app/markets/${r.market_id}`}
              accent="rose"
            />
          )}
        />

        <DepList
          testid={`${testid}-category`}
          title="Same category"
          icon={GitBranch}
          empty="No sibling category markets."
          items={data.same_category_markets || []}
          render={(r) => (
            <RowInner
              title={r.market_title}
              subtitle={`${r.category}${r.volume_24h ? ` · ${fmtUSD(r.volume_24h)} 24h` : ""}`}
              to={`/app/markets/${r.market_id}`}
            />
          )}
        />

        <DepList
          testid={`${testid}-correlated`}
          title="Price co-movement"
          icon={TrendingUp}
          empty="Need more price samples to detect correlation."
          items={data.price_correlated_markets || []}
          render={(r) => (
            <RowInner
              title={r.market_title}
              subtitle={`ρ = ${r.correlation > 0 ? "+" : ""}${r.correlation.toFixed(2)} · n=${r.samples}`}
              to={`/app/markets/${r.market_id}`}
              accent={r.correlation > 0 ? "emerald" : "rose"}
            />
          )}
        />
      </div>
    </section>
  );
}

function Header() {
  return (
    <div className="flex items-center gap-2">
      <GitBranch size={16} className="text-zinc-300" />
      <h3 className="font-heading font-semibold text-zinc-100 text-lg">Market dependencies</h3>
    </div>
  );
}

function DepList({ title, icon: Icon, items, empty, render, testid }) {
  return (
    <div className="p-4" data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        <Icon size={11} /> {title}
      </div>
      {items.length === 0 && <div className="text-xs text-zinc-500 py-1">{empty}</div>}
      <ul className="space-y-1">
        {items.slice(0, 6).map((r) => (
          <li key={r.market_id}>{render(r)}</li>
        ))}
      </ul>
    </div>
  );
}

function RowInner({ title, subtitle, to, accent }) {
  const borderCls =
    accent === "rose"    ? "border-l-2 border-rose-500/40 pl-2" :
    accent === "emerald" ? "border-l-2 border-emerald-500/40 pl-2" : "pl-2";
  return (
    <Link to={to} className={`block ${borderCls} py-1 rounded-sm hover:bg-zinc-900/40`}>
      <div className="text-sm text-zinc-100 line-clamp-1">{title}</div>
      <div className="text-[11px] text-zinc-500 line-clamp-1">{subtitle}</div>
    </Link>
  );
}
